import {useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getVclidCookie} from 'modules/cookies/selectors'
import {getAnalyticsPageName, getAnonymousId} from 'modules/meta/selectors'
import {
  getDeepLinkSearchParameters,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {
  getEpvUrlParam,
  getEsdUrlParam,
  getIsUserLocationSearch,
  getLandingQueryString
} from 'modules/search/selectors'

import {
  getPersistedMediaSource,
  getUtmSource
} from '@vio/core/src/_web/sapiLabel/business'
import {Page} from '@vio/core/src/analytics/types/Events'
import {generateAppDownloadUrl} from '@vio/core/src/deepLinking/generateAppDownloadUrl'
import {
  DeepLinkSearchParams,
  OneLinkURLs,
  Placement
} from '@vio/core/src/deepLinking/types'
import {SearchParameters} from '@vio/core/src/sapi/types'

type DeepLinkSearchParamsType = Pick<
  SearchParameters,
  'checkIn' | 'checkOut' | 'hotelId' | 'placeId' | 'rooms' | 'searchId'
>

/**
 * Maps language codes to Apple App Store country codes.
 * Apple URLs use country codes (e.g., /us/, /gb/), not language codes.
 * Only languages where the base language code differs from the country code need an entry.
 * Unmapped codes (de, fr, nl, it, es, pt, pl, fi, hu, ro, ru, tr, th, id) match by coincidence.
 */
const appStoreCountryCodeMap: Record<string, string> = {
  en: 'us',
  ar: 'sa',
  he: 'il',
  ko: 'kr',
  ja: 'jp',
  zh: 'cn',
  sv: 'se',
  da: 'dk',
  cs: 'cz',
  el: 'gr',
  vi: 'vn',
  uk: 'ua',
  ms: 'my',
  no: 'no'
}

/**
 * The smart script normally hands back a URL that already carries a query
 * string, but its server-side fallback is a bare OneLink, and appending `&` to
 * that produces a different path rather than a parameter.
 */
const appendAfRedirect = (url: string, redirectUrl: string) => {
  const separator = url.includes('?') ? '&' : '?'

  return `${url}${separator}af_r=${encodeURIComponent(redirectUrl)}`
}

const useGenerateAppDownloadUrl = (currentPage?: Page) => {
  const anonymousId = useSelector(getAnonymousId)
  const searchParameters: DeepLinkSearchParamsType = useSelector(
    getDeepLinkSearchParameters
  )
  const isUserLocationSearch = useSelector(getIsUserLocationSearch)
  const esd = useSelector(getEsdUrlParam)
  const epv = useSelector(getEpvUrlParam)
  const vclid = useSelector(getVclidCookie)
  const showTotalPrices = useSelector(getShowTotalPrices)
  const page = useSelector(getAnalyticsPageName)
  const landingQueryString = useSelector(getLandingQueryString)
  const mediaSource = useMemo(() => getPersistedMediaSource(), [])
  // Read on every render, not memoised: the SAPI label cookie is written after the first render
  const utmSource = getUtmSource(landingQueryString)

  const generateAppLockedDealDeepLink = useCallback(
    (placement: Placement, params?: DeepLinkSearchParams) => {
      const {clickedOfferRate, currency, ...other} = params || {}
      return generateAppDownloadUrl(
        OneLinkURLs.WEB_TO_APP_OFFERS,
        currentPage || page,
        placement,
        {
          ...searchParameters,
          userLocationSearch: isUserLocationSearch ? 1 : 0,
          showTotal: showTotalPrices ? 1 : 0,
          vclid,
          esd,
          epv,
          anonymousId,
          mediaSource,
          utm_source: utmSource,
          currency,
          ...(clickedOfferRate ? {forceCurrencyChange: 1} : {}),
          clickedOfferRate,
          ...other
        }
      )
    },
    [
      anonymousId,
      currentPage,
      epv,
      esd,
      isUserLocationSearch,
      mediaSource,
      utmSource,
      page,
      searchParameters,
      showTotalPrices,
      vclid
    ]
  )

  const generateSearchDeepLink = useCallback(
    (placement: Placement, params?: DeepLinkSearchParams) => {
      const {currency, clickedOfferRate, ...other} = params || {}
      return generateAppDownloadUrl(
        OneLinkURLs.GENERAL,
        currentPage || page,
        placement,
        {
          ...searchParameters,
          userLocationSearch: isUserLocationSearch ? 1 : 0,
          showTotal: showTotalPrices ? 1 : 0,
          vclid,
          esd,
          epv,
          anonymousId,
          mediaSource,
          utm_source: utmSource,
          currency,
          ...(clickedOfferRate ? {forceCurrencyChange: 1} : {}),
          clickedOfferRate,
          ...other
        }
      )
    },
    [
      anonymousId,
      currentPage,
      epv,
      esd,
      isUserLocationSearch,
      mediaSource,
      utmSource,
      page,
      searchParameters,
      showTotalPrices,
      vclid
    ]
  )

  const generateAppStoreDeepLink = useCallback(
    (
      placement: Placement,
      params?: DeepLinkSearchParams,
      redirectLanguage?: string
    ) => {
      const url = generateAppDownloadUrl(
        OneLinkURLs.APP_STORE,
        currentPage || page,
        placement,
        {
          vclid,
          anonymousId,
          mediaSource,
          utm_source: utmSource,
          ...params
        }
      )

      if (redirectLanguage == null) return url

      const baseLang = redirectLanguage.split('-')[0]
      const countryCode = appStoreCountryCodeMap[baseLang] || baseLang
      const appStoreRedirectUrl = `https://apps.apple.com/${countryCode}/app/id1639101398`

      return appendAfRedirect(url, appStoreRedirectUrl)
    },
    [anonymousId, currentPage, mediaSource, page, utmSource, vclid]
  )

  const generatePlayStoreDeepLink = useCallback(
    (
      placement: Placement,
      params?: DeepLinkSearchParams,
      redirectLanguage?: string
    ) => {
      const url = generateAppDownloadUrl(
        OneLinkURLs.PLAY_STORE,
        currentPage || page,
        placement,
        {
          vclid,
          anonymousId,
          mediaSource,
          utm_source: utmSource,
          ...params
        }
      )

      if (redirectLanguage == null) return url

      const lang = redirectLanguage.split('-')[0]
      const playStoreRedirectUrl = `https://play.google.com/store/apps/details?id=com.vio.mobileapp&hl=${lang}`

      return appendAfRedirect(url, playStoreRedirectUrl)
    },
    [anonymousId, currentPage, mediaSource, page, utmSource, vclid]
  )

  const generateGeneralDeepLink = useCallback(
    (placement: Placement, params?: DeepLinkSearchParams) => {
      return generateAppDownloadUrl(
        OneLinkURLs.GENERAL,
        currentPage || page,
        placement,
        {
          vclid,
          anonymousId,
          mediaSource,
          utm_source: utmSource,
          ...params
        }
      )
    },
    [anonymousId, currentPage, mediaSource, page, utmSource, vclid]
  )

  return {
    generateAppLockedDealDeepLink,
    generateSearchDeepLink,
    generateGeneralDeepLink,
    generateAppStoreDeepLink,
    generatePlayStoreDeepLink
  }
}

export default useGenerateAppDownloadUrl
